import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../Common/Contexts/LanguageContext'
import { TeamsAuth as TeamsAuthLanguage } from '../../../../i18n'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { MessageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { TelSysAdminDTO } from '../../../NonReact/Types/Types'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { OAuthButton } from '../../Common/Components/OAuthButton/OAuthButton'
import { OAuthConfig } from '../../Common/Components/OAuthButton/Types/Types'
import { getOAuthConfig } from '../../Common/API/ConfigAPI'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { useBranding } from '../../Common/Hooks/useBranding'
import { Branding } from '../../Common/API/BrandingAPI'
import Button from '../../Common/Components/Button'
import Icon, { IconName } from '../../Common/Components/Icon'
import Loader from '../../Common/Components/Loader'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { telSysUpdateURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { signOut } from '../../Common/API/UserSignInOutAPI'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { DarkModeType, useDarkMode } from '../../Common/Hooks/useDarkMode'
import { configureFullURLForLocalDevelopment } from '../../Common/Utils/LocalUtils'
import { AUTH_ROUTE_PATH } from '../../../ReactRouting/Constants/Constants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { getTelSys } from '../../../NonReact/common/API/EntityAPIClient'

type Props = {
    isRouterMode?: boolean
}

export const TeamsAuth: FC<Props> = ({ isRouterMode }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(TeamsAuthLanguage.NAME)
    const { displayError }: MessageContextType = useContext(MessageContext)

    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const branding: Nullable<Branding> = useBranding()

    let navigate: Nullable<NavigateFunction> = null
    let location: Nullable<Location> = null

    if (isRouterMode) {
        navigate = useNavigate()
        location = useLocation()
    }

    const { isDarkMode }: DarkModeType = useDarkMode()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [error, setError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isAuthenticated, setIsAuthenticated]: ReactState<boolean> = useState<boolean>(false)
    const [isAuthenticating, setIsAuthenticating]: ReactState<boolean> = useState<boolean>(false)
    const [config, setConfig]: ReactState<Nullable<OAuthConfig>> = useState<Nullable<OAuthConfig>>()
    const [telSys, setTelSys]: ReactState<Nullable<TelSysAdminDTO>> = useState<Nullable<TelSysAdminDTO>>()
    const [statusesToDisplay, setStatusesToDisplay]: ReactState<Nullable<ReactElement[]>> =
        useState<Nullable<ReactElement[]>>()

    const mainLogoURL: string = useMemo((): string => {
        if (isRouterMode) {
            return (
                (isDarkMode
                    ? branding?.BrandingConfig?.imagery?.mainLogo
                    : branding?.BrandingConfig?.imagery?.mainLogoForWhiteBG) ?? EMPTY_STRING
            )
        }

        return branding?.LogoURL ?? EMPTY_STRING
    }, [isRouterMode, isDarkMode, branding])

    const onAuthenticating = (): void => {
        setError(undefined)
        setIsAuthenticated(false)
        setIsAuthenticating(true)
    }

    const onOAuthFailure = (e?: Error): void => {
        setError(e?.message ?? language.get(TeamsAuthLanguage.TeamsAuth_Error_Unknown))
        setIsAuthenticating(false)
    }

    const onOAuthSuccess = useCallback(
        (sessionID?: number): void => {
            if (telSys?.TelSysID) {
                requestJSON<void, TelSysAdminDTO>(telSysUpdateURL(telSys?.TelSysID), {
                    method: RESTMethods.PUT,
                    body: {
                        ...telSys,
                        ...(IsVarEmpty(telSys?.Advanced)
                            ? {}
                            : {
                                  Advanced: {
                                      ...telSys.Advanced,
                                      MaximumAllowedUsersMonitored: undefined,
                                      MaximumAllowedUsersSync: undefined
                                  }
                              }),
                        AuthPassword2OAuthSessionID: sessionID?.toString()
                    }
                })
                    .then((): void => {
                        if (isMounted.current) {
                            setIsAuthenticating(false)
                            setIsAuthenticated(true)

                            setTimeout(
                                (): void =>
                                    signOut(
                                        isRouterMode
                                            ? (): void =>
                                                  navigate?.(AUTH_ROUTE_PATH, {
                                                      replace: true,
                                                      state: { location }
                                                  })
                                            : undefined
                                    ),
                                1000
                            )
                        }
                    })
                    .catch(onOAuthFailure)
            } else {
                onOAuthFailure()
            }
        },
        [telSys]
    )

    useEffect((): void => {
        if (userInfo) {
            if (
                (userInfo?.Scope?.TelSysID?.length ?? 0) !== 1 ||
                userInfo?.Scope?.TelSysID[0] === ALL_VALUE ||
                !PermissionsUtil.hasPermissions(
                    userInfo,
                    PermissionsUtil.builder(AdminBackendPermissionNames.TEL_SYS).update().read()
                )
            ) {
                return signOut(
                    isRouterMode
                        ? (): void =>
                              navigate?.(AUTH_ROUTE_PATH, {
                                  replace: true,
                                  state: { location }
                              })
                        : undefined
                )
            }

            getTelSys(userInfo?.Scope?.TelSysID[0])
                .then((telephonyServer: Nullable<TelSysAdminDTO>): void => {
                    if (telephonyServer?.CommsEnabled && telephonyServer?.TelSysType === TelSysTypes.MS_TEAMS) {
                        setTelSys(telephonyServer)
                    } else {
                        signOut(
                            isRouterMode
                                ? (): void =>
                                      navigate?.(AUTH_ROUTE_PATH, {
                                          replace: true,
                                          state: { location }
                                      })
                                : undefined
                        )
                    }
                })
                .catch((e: Error): void => {
                    displayError(e)
                    setTimeout(signOut, 2000)
                })

            getOAuthConfig(TelSysTypes.MS_TEAMS).then(setConfig).catch(displayError)
        }

        return undefined
    }, [userInfo, isRouterMode])

    return (
        <div
            className={`${isRouterMode ? 'router-teams-auth' : 'teams-auth-content'} ${
                isAuthenticating ? 'is-authenticating' : ''
            } ${isAuthenticated ? 'is-authenticated' : ''} ${isStringNotEmpty(error) ? 'is-error' : ''}`}
        >
            <div className='ta-logo-container'>
                <img className='ta-logo' src={configureFullURLForLocalDevelopment(mainLogoURL)} alt='Company Logo' />

                {isAuthenticating ? <Loader className='authenticating-loader' /> : <></>}

                <div className='link-icon-container'>
                    <Icon iconName={isAuthenticated ? IconName.LINKED : IconName.NOT_LINKED} />
                </div>

                <img className='ta-logo' src='/CCS/Styles/Images/Teams/teams-logo.png' alt='Teams Logo' />
            </div>

            <div className='ta-intro ta-intro-1'>
                {LanguageUtils.formatLanguageEntry(
                    language.getInterpolated(
                        TeamsAuthLanguage.TeamsAuth_UserIntro,
                        isStringNotEmpty(userInfo?.FullName) ? userInfo?.FullName : userInfo?.Username
                    )
                )}
            </div>

            <div className='ta-intro'>{language.get(TeamsAuthLanguage.TeamsAuth_UserIntro_2)}</div>

            {isStringNotEmpty(error) && IsVarEmpty(statusesToDisplay) ? <div className='ta-error'>{error}</div> : <></>}

            {isVarNotEmpty(statusesToDisplay) && statusesToDisplay.length > 0 ? (
                <div className='ta-statuses'>
                    {statusesToDisplay.map(
                        (status: ReactElement, index: number): ReactElement => (
                            <div key={`status-${index}`} className='ta-status'>
                                {status}
                            </div>
                        )
                    )}
                </div>
            ) : (
                <></>
            )}

            {isVarNotEmpty(config) && isVarNotEmpty(telSys) ? (
                <div className={`ta-action-container`}>
                    {isRouterMode ? (
                        <RoundedButton
                            className='ta-decline'
                            onClick={(): void =>
                                signOut(
                                    isRouterMode
                                        ? (): void =>
                                              navigate?.(AUTH_ROUTE_PATH, {
                                                  replace: true,
                                                  state: { location }
                                              })
                                        : undefined
                                )
                            }
                            isOutlined
                            disabled={isAuthenticating || isAuthenticated}
                        >
                            {language.get(TeamsAuthLanguage.TeamsAuth_Decline)}
                        </RoundedButton>
                    ) : (
                        <Button
                            customClass='ta-decline'
                            label={language.get(TeamsAuthLanguage.TeamsAuth_Decline)}
                            onClick={(): void => signOut()}
                            isOutlined
                            isDisabled={isAuthenticating || isAuthenticated}
                        />
                    )}
                    <OAuthButton
                        selectedTelSys={telSys?.TelSysID}
                        integrationType={TelSysTypes.MS_TEAMS}
                        config={config}
                        onAuthenticating={onAuthenticating}
                        onOAuthDisplayStatus={
                            isRouterMode
                                ? (statuses: ReactElement[]): void => setStatusesToDisplay(statuses)
                                : undefined
                        }
                        onOAuthSuccess={onOAuthSuccess}
                        onOAuthFailure={onOAuthFailure}
                        oAuthMessageOptions={{
                            preAuthorisationButtonTitle: language.get(TeamsAuthLanguage.TeamsAuth_AdminConsent),
                            preAuthorisedMessage: language.get(TeamsAuthLanguage.TeamsAuth_ConsentedMessage),
                            authorisationButtonTitle: language.get(TeamsAuthLanguage.TeamsAuth_Authorise),
                            authorisedMessage: language.get(TeamsAuthLanguage.TeamsAuth_AuthorisedMessage)
                        }}
                        isRouterMode={isRouterMode}
                    />
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
